import { useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { updateBranch } from "../../services/branch";
import {
  BranchConflictError,
  BranchErrorResponse,
  BranchResponse,
} from "../../types/branch.type";
import BranchFormFields from "./BranchFormFields";
import {
  EDIT_FIELDS,
  LOCKED_EDIT_FIELDS,
  buildBranchUpdatePayload,
  mapBranchToForm,
  validateBranchForm,
} from "./branchForm";
import { resolveBranchSaveFailure } from "./branchStatus";
import { UPDATE_FAILED_MESSAGE } from "./constants";
import { useBranchForm } from "./useBranchForm";

interface BranchInfoFormProps {
  branch: BranchResponse;
  /** Called after a successful save so the page reloads the branch. */
  onSaved: () => void;
}

/**
 * Master's edit form. Only the fields that changed are sent. A legacy branch may hold
 * nulls (or old formats) in fields the form now requires; those must be completed before
 * any save, under the same rules as create.
 */
export default function BranchInfoForm({ branch, onSaved }: BranchInfoFormProps) {
  const initialValues = useMemo(() => mapBranchToForm(branch), [branch]);
  const { values, errors, setErrors, handleFieldChange, reset, validate } =
    useBranchForm(initialValues);
  const [saving, setSaving] = useState<boolean>(false);
  const [failureMessage, setFailureMessage] = useState<string>("");

  useEffect(() => {
    reset(initialValues);
    setFailureMessage("");
  }, [initialValues, reset]);

  const payload = useMemo(
    () => buildBranchUpdatePayload(initialValues, values),
    [initialValues, values],
  );
  const hasChanges = Object.keys(payload).length > 0;
  const isStoredInfoIncomplete = useMemo(
    () => Object.keys(validateBranchForm(initialValues, EDIT_FIELDS)).length > 0,
    [initialValues],
  );

  const handleSave = async () => {
    if (!validate(EDIT_FIELDS)) return;
    setSaving(true);
    setFailureMessage("");
    try {
      await updateBranch(branch.uuid, payload);
      onSaved();
    } catch (error) {
      const response = isAxiosError<BranchErrorResponse<BranchConflictError>>(error)
        ? error.response
        : undefined;
      const failure = resolveBranchSaveFailure(
        response?.status,
        response?.data,
        UPDATE_FAILED_MESSAGE,
      );
      setFailureMessage(failure.message);
      setErrors(failure.fieldErrors);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    reset(initialValues);
    setFailureMessage("");
  };

  return (
    <Stack gap={2}>
      {isStoredInfoIncomplete && (
        <Alert severity="warning">
          ข้อมูลสาขายังไม่ครบหรือไม่ตรงรูปแบบ กรุณากรอกช่องที่บังคับให้ครบก่อนบันทึก
        </Alert>
      )}
      <BranchFormFields
        values={values}
        errors={errors}
        lockedFields={LOCKED_EDIT_FIELDS}
        onFieldChange={handleFieldChange}
      />
      {failureMessage && <Alert severity="error">{failureMessage}</Alert>}
      <Stack direction="row" justifyContent="flex-end" gap={1}>
        <Button disabled={!hasChanges || saving} onClick={handleDiscard}>
          ยกเลิกการแก้ไข
        </Button>
        <Button
          variant="contained"
          loading={saving}
          disabled={!hasChanges}
          onClick={handleSave}
        >
          บันทึก
        </Button>
      </Stack>
    </Stack>
  );
}
